import { useState, useEffect, useMemo } from 'react';

interface QueryResults {
  isExtraSmall: boolean;
  isSmall: boolean;
  isExtraSmallAndSmall: boolean;
  isMedium: boolean;
  isLarge: boolean;
}

type Queries = {
  [K in keyof QueryResults]: string;
};

type QueryMatches = {
  [K in keyof QueryResults]: MediaQueryList;
};

const DEFAULT_QUERIES: Queries = {
  isExtraSmall: '(max-width: 374px)',
  isSmall: '(max-width: 767px) and (min-width: 375px)',
  isExtraSmallAndSmall: '(max-width: 767px)',
  isMedium: '(min-width: 768px) and (max-width: 1023px)',
  isLarge: '(min-width: 1024px)',
};

const serverSideDefaults: QueryResults = {
  isExtraSmall: true,
  isSmall: true,
  isExtraSmallAndSmall: true,
  isMedium: false,
  isLarge: false,
};

function listenOnMediaMatch(mql, onMediaMatch) {
  mql.addListener(onMediaMatch);
  return () => {
    mql.removeListener(onMediaMatch);
  };
}

/* Get the result of media match as needed */
function createMatches(matches: QueryMatches) {
  return Object.entries(matches).reduce(
    (state, [key, watch]) => ({ ...state, [key]: watch.matches }),
    {} as QueryResults
  );
}

function useIsViewportSize(queries = DEFAULT_QUERIES): QueryResults {
  /* Create singleton list of media queries */
  const mediaMatchMap = useMemo(() => {
    if (typeof window !== 'undefined') {
      return Object.entries(queries).reduce(
        (state, [key, query]: [keyof QueryResults, string]) => ({
          ...state,
          [key]: window.matchMedia(query),
        }),
        {} as QueryMatches
      );
    }
    return null;
  }, [queries]);

  /* We need to initially render with the server side defaults so there is
   * no mismatch between the server rendered content and initial client content */
  const [isViewportSize, setIsViewportSize] = useState(serverSideDefaults);

  useEffect(() => {
    /* add listerners for each match and re-run createMatches as needed */
    const destroyListeners = Object.values(mediaMatchMap).map((watch) =>
      listenOnMediaMatch(watch, () => {
        setIsViewportSize(createMatches(mediaMatchMap));
      })
    );

    setIsViewportSize(createMatches(mediaMatchMap));

    return () => {
      destroyListeners.map((fn) => fn?.());
    };
  }, [mediaMatchMap]);

  return isViewportSize;
}

export default useIsViewportSize;
